import React, { useRef, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowCircleUp, faArrowCircleDown } from '@fortawesome/free-solid-svg-icons';
import './dropdown-fix.css';

/**
 * A custom dropdown component with single selection and accessibility features
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the dropdown is open
 * @param {Function} props.toggleDropdown - Function to toggle dropdown
 * @param {string} props.selectedOption - Currently selected option
 * @param {Array} props.options - Array of options to display in the dropdown
 * @param {Function} props.onSelect - Function called when an option is selected (option) => void
 * @param {Object} props.themeStyles - Object containing theme-specific styles
 * @param {boolean} props.isDarkMode - Whether the application is in dark mode
 * @param {string} props.label - ARIA label for the dropdown
 * @param {number} props.uiScale - Scale factor for UI sizing (default 1)
 */
const SingleSelectDropdown = ({
  isOpen,
  toggleDropdown,
  selectedOption,
  options,
  onSelect,
  themeStyles,
  isDarkMode,
  label = "Select an option",
  uiScale = 1
}) => {
  const dropdownRef = useRef(null);
  const menuRef = useRef(null);
  const headerRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState('bottom');
  const [dropdownWidth, setDropdownWidth] = useState('auto');
  const [isInSimulateExam, setIsInSimulateExam] = useState(false);
  const [isInPracticeTest, setIsInPracticeTest] = useState(false);

  // UI scaling calculations
  const s = uiScale ?? 1;
  const baseFontSize = Math.round(14 * s);
  const basePaddingY = Math.round(10 * s);
  const basePaddingX = Math.round(12 * s);
  const baseIconSize = Math.round(14 * s);
  const baseBorderRadius = Math.round(5 * s);
  const baseItemHeight = Math.round(46 * s);
  const baseItemPaddingY = Math.round(12 * s);
  const baseItemPaddingX = Math.round(15 * s);
  const baseMinHeight = Math.round(22 * s);

  // Handle click outside to close dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (isOpen && dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        toggleDropdown();
      }
    }
    
    // Handle scrolling to reposition the dropdown if needed
    function handleScroll() {
      if (isOpen && menuRef.current && headerRef.current) {
        const headerRect = headerRef.current.getBoundingClientRect();
        
        // Update position based on current header position
        if (dropdownPosition === 'bottom') {
          menuRef.current.style.top = `${headerRect.bottom + 5}px`;
          menuRef.current.style.left = `${headerRect.left}px`;
        } else {
          menuRef.current.style.bottom = `${window.innerHeight - headerRect.top + 5}px`;
          menuRef.current.style.left = `${headerRect.left}px`;
        }
      }
    }
    
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleScroll);
      window.addEventListener('resize', handleScroll);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [isOpen, toggleDropdown, dropdownPosition]);

  // Prevent body scrolling when dropdown is open (but only for touchscreen devices)
  useEffect(() => {
    if (isOpen) {
      // Store original scroll position
      const scrollY = window.scrollY;
      
      // Function to prevent default touch move to stop scrolling while ensuring dropdown still works
      const preventScroll = (e) => {
        // Don't prevent scrolling inside the dropdown itself
        if (menuRef.current && menuRef.current.contains(e.target)) {
          return;
        }
        window.scrollTo(0, scrollY);
      };
      
      // Only add for touch devices
      if ('ontouchstart' in window) {
        window.addEventListener('scroll', preventScroll, { passive: false });
      }
      
      return () => {
        if ('ontouchstart' in window) {
          window.removeEventListener('scroll', preventScroll);
        }
      };
    }
  }, [isOpen]);
  // Calculate the width needed for the longest item
  const calculateOptimalWidth = () => {
    if (!isOpen || !options || options.length === 0 || !dropdownRef.current) return;

    // Create a temporary hidden div to measure text width more accurately
    const tempDiv = document.createElement('div');
    tempDiv.style.visibility = 'hidden';
    tempDiv.style.position = 'absolute';
    tempDiv.style.left = '-9999px';
    tempDiv.style.fontFamily = window.getComputedStyle(dropdownRef.current).fontFamily;
    tempDiv.style.fontSize = window.getComputedStyle(dropdownRef.current).fontSize;
    tempDiv.style.fontWeight = window.getComputedStyle(dropdownRef.current).fontWeight;
    
    document.body.appendChild(tempDiv);
    let maxWidth = 0;
    
    options.forEach(option => {
      tempDiv.textContent = option;
      const width = tempDiv.getBoundingClientRect().width;
      maxWidth = Math.max(maxWidth, width);
    });
    
    // Calculate the width of exactly 3 characters for padding
    tempDiv.textContent = 'WWW'; // Using W as it's typically a wide character
    const threeCharWidth = tempDiv.getBoundingClientRect().width;
    
    document.body.removeChild(tempDiv);
    
    // Get the current dropdown button width as minimum width
    const headerWidth = headerRef.current ? headerRef.current.offsetWidth : 0;
    
    // Total width needed is: text width + padding (exactly 3 characters width)
    const optimalWidth = Math.max(headerWidth, maxWidth + threeCharWidth);
    
    setDropdownWidth(optimalWidth);
    
    // Position the menu properly based on the header's position
    if (menuRef.current && headerRef.current) {
      const headerRect = headerRef.current.getBoundingClientRect();
      if (dropdownPosition === 'bottom') {
        menuRef.current.style.top = `${headerRect.bottom + 5}px`;
        menuRef.current.style.left = `${headerRect.left}px`;
      } else {
        menuRef.current.style.bottom = `${window.innerHeight - headerRect.top + 5}px`;
        menuRef.current.style.left = `${headerRect.left}px`;
      }
    }
  };// Check for available space and position dropdown accordingly
  useEffect(() => {
    if (isOpen && dropdownRef.current && menuRef.current && headerRef.current) {      const headerRect = headerRef.current.getBoundingClientRect();
      const dropdownItemHeight = baseItemHeight; // Approximate height of each item with padding
      const visibleItemsLimit = 5; // Limit to show only 5 items at once
      const maxVisibleHeight = dropdownItemHeight * visibleItemsLimit; // Height for 5 items
      const menuHeight = options.length <= visibleItemsLimit ? 
                         options.length * dropdownItemHeight : 
                         maxVisibleHeight; // Calculate exact height based on number of items
      const spaceBelow = window.innerHeight - headerRect.bottom;
      
      // Fix for SimulateExam component - force position directly under header
      // Update the state variables for component context
      const inSimulateExam = document.querySelector('.simulate-exam') && dropdownRef.current.closest('.simulate-exam');
      const inPracticeTest = document.querySelector('.practice-test') && dropdownRef.current.closest('.practice-test');
      setIsInSimulateExam(inSimulateExam);
      setIsInPracticeTest(inPracticeTest);
      
      if (inSimulateExam || inPracticeTest) {
        setDropdownPosition('bottom');
        // Set this specifically for SimulateExam or PracticeTest
        menuRef.current.style.position = 'absolute';
        menuRef.current.style.top = `${headerRect.bottom}px`;
        menuRef.current.style.left = `${headerRect.left}px`;
      }
      // If there's not enough space below, position above if there's more space there
      else if (spaceBelow < menuHeight && headerRect.top > menuHeight) {
        setDropdownPosition('top');
      } else {
        setDropdownPosition('bottom');
      }
        // Calculate optimal width when dropdown opens
      calculateOptimalWidth();
      
      // After width is calculated and menu is open, check if it extends off-screen
      setTimeout(() => {
        if (menuRef.current) {
          const menuRect = menuRef.current.getBoundingClientRect();
          const viewportWidth = window.innerWidth;
          
          // If menu extends beyond right edge of viewport, adjust position
          if (menuRect.right > viewportWidth) {
            // Calculate how much to shift left to stay in viewport
            const overflowAmount = menuRect.right - viewportWidth;
            const newLeft = Math.max(0, headerRect.left - overflowAmount - 10); // 10px buffer
            menuRef.current.style.left = `${newLeft}px`;
          }
        }
      }, 0);
    }
  }, [isOpen, options]);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      // Toggle dropdown on Enter or Space
      e.preventDefault();
      toggleDropdown();
    } else if (e.key === 'Escape' && isOpen) {
      // Close dropdown on Escape
      e.preventDefault();
      toggleDropdown();
    } else if (e.key === 'ArrowDown' && isOpen) {
      // Focus the first item when pressing down arrow
      e.preventDefault();
      const firstItem = dropdownRef.current.querySelector('.dropdown-item');
      if (firstItem) firstItem.focus();
    }
  };

  // Handle keyboard navigation within dropdown items
  const handleItemKeyDown = (e, option, index) => {
    if (e.key === 'Enter' || e.key === ' ') {
      // Select item on Enter or Space
      e.preventDefault();
      onSelect(option);
      toggleDropdown();
    } else if (e.key === 'ArrowDown') {
      // Move focus to next item
      e.preventDefault();
      const nextItem = e.currentTarget.nextElementSibling;
      if (nextItem) nextItem.focus();
    } else if (e.key === 'ArrowUp') {
      // Move focus to previous item
      e.preventDefault();
      const prevItem = e.currentTarget.previousElementSibling;
      if (prevItem) {
        prevItem.focus();
      } else {
        // If at first item, go back to dropdown header
        dropdownRef.current.querySelector('.dropdown-header').focus();
      }
    } else if (e.key === 'Home') {
      // Move to first item
      e.preventDefault();
      dropdownRef.current.querySelector('.dropdown-item').focus();
    } else if (e.key === 'End') {
      // Move to last item
      e.preventDefault();
      const items = dropdownRef.current.querySelectorAll('.dropdown-item');
      items[items.length - 1].focus();
    } else if (e.key === 'Escape') {
      // Close dropdown and return focus to header
      e.preventDefault();
      toggleDropdown();
      dropdownRef.current.querySelector('.dropdown-header').focus();
    }
  };

  return (
    <div 
      className="custom-dropdown" 
      ref={dropdownRef} 
      style={{ 
        position: 'relative',
        '--dd-menu-pad-y': `${Math.round(5 * s)}px`,
        '--dd-menu-pad-x': '0px',
        '--dd-checkbox-size': `${Math.round(22 * s)}px`,
        '--dd-checkbox-radius': `${Math.round(3 * s)}px`,
        '--dd-checkbox-margin': `${Math.round(12 * s)}px`,
        '--dd-hierarchy-left': `${Math.round(12 * s)}px`,
        '--dd-hierarchy-width': `${Math.round(1 * s)}px`,
        '--dd-branch-width': `${Math.round(8 * s)}px`,
        '--dd-branch-height': `${Math.round(1 * s)}px`
      }}
    >
      {/* Dropdown trigger button */}      <div 
        className="dropdown-header"
        ref={headerRef}
        onClick={toggleDropdown}
        onKeyDown={handleKeyDown}
        tabIndex="0"
        role="button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={label}
        style={{
          background: themeStyles.select.background,
          color: themeStyles.select.color,
          border: themeStyles.select.border,
          borderRadius: `${baseBorderRadius}px`,
          padding: `${basePaddingY}px ${basePaddingX}px`,
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          position: 'relative',
          boxShadow: '0 2px 5px rgba(0, 0, 0, 0.05)',
          transition: 'all 0.2s ease',
          outline: 'none',
          width: '200px' // Ensure the header takes full width of parent
        }}
      >
        <span style={{ marginRight: `${Math.round(5 * s)}px` }}>
          {selectedOption}
        </span>
        <FontAwesomeIcon 
          icon={isOpen ? faArrowCircleUp : faArrowCircleDown} 
          style={{ fontSize: `${baseIconSize}px` }}
        />
      </div>
        {/* Dropdown menu */}
      {isOpen && (
        <div          className="dropdown-menu"
          ref={menuRef}
          role="listbox"          style={{
            // Position conditionally based on whether we're in SimulateExam/PracticeTest or not
            position: (isInSimulateExam || isInPracticeTest) ? 'absolute' : 'fixed',
            ...(dropdownPosition === 'bottom' 
              ? { top: headerRef.current ? headerRef.current.getBoundingClientRect().bottom + 5 : 0 }
              : { bottom: window.innerHeight - (headerRef.current ? headerRef.current.getBoundingClientRect().top : 0) + 5 }),
            left: headerRef.current ? headerRef.current.getBoundingClientRect().left : 0,            minWidth: headerRef.current ? headerRef.current.offsetWidth : '100%',
            zIndex: 9999, // Ensure highest z-index
            width: dropdownWidth === 'auto' ? 'auto' : `${dropdownWidth}px`,
            maxWidth: `min(${dropdownWidth === 'auto' ? 300 : dropdownWidth}px, calc(100vw - 40px))`,
            background: isDarkMode ? '#1c2740' : '#fff',
            border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid #dcdfe6',
            borderRadius: `${baseBorderRadius}px`,
            maxHeight: options.length <= 5 ? 'auto' : `${baseItemHeight * 5}px`,
            overflowY: options.length <= 5 ? 'visible' : 'auto',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
          }}
        >
          {options.map((option, index) => (
            <div 
              key={option}
              className="dropdown-item"
              onClick={() => {
                onSelect(option);
                toggleDropdown();
              }}
              onKeyDown={(e) => handleItemKeyDown(e, option, index)}
              role="option"
              aria-selected={selectedOption === option}
              tabIndex="0"              style={{
                padding: `${baseItemPaddingY}px ${baseItemPaddingX}px`,
                cursor: 'pointer',
                borderBottom: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.05)',
                display: 'flex',
                alignItems: 'center',
                transition: 'background 0.2s',
                background: selectedOption === option 
                  ? (isDarkMode ? 'rgba(61, 139, 255, 0.18)' : 'rgba(25, 118, 210, 0.1)')
                  : 'transparent',
                fontSize: `${baseFontSize}px`,
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                color: themeStyles.text.primary,
                outline: 'none',
                minHeight: `${baseMinHeight}px`,
                boxSizing: 'border-box'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = isDarkMode 
                  ? 'rgba(255, 255, 255, 0.1)' 
                  : 'rgba(0, 0, 0, 0.03)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = selectedOption === option
                  ? (isDarkMode ? 'rgba(61, 139, 255, 0.18)' : 'rgba(25, 118, 210, 0.1)')
                  : 'transparent';
              }}
            >
              {option}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SingleSelectDropdown;
